import { MainLayout } from '../../Components/Layouts/MainLayout/MainLayout';
import { getCandidateAreas, getCandidates } from '../../Services/Candidates';
import { useTranslation } from 'react-i18next';
import SearchBar from '../../Components/SearchBar/SearchBar';
import { useDebounce } from '../../Hooks/useDebounce';
import { useCallback, useEffect, useState } from 'react';
import AutocompleteSelect from '../../Components/AutocompleteSelect/AutocompleteSelect';
import { getSkills } from '../../Services/Skills';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Autocomplete, Box, CircularProgress, Paper, TextField } from '@mui/material';
import { ClearFiltersButton } from '../../Components/Common/ClearFiltersButton/ClearFiltersButton';
import { COUNT_ROWS_PER_PAGE, ENGLISH_LEVELS } from '../../Types/constants';
import { Loader } from '../../Components/Loader/Loader';
import { CandidateTable } from './Components/CandidateTable/CandidateTable';
import { DateRangePicker } from '../../Components/Common/DateRangePicker/DateRangePicker';

export const Candidates = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [skillsSearchTerm, setSkillsSearchTerm] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [clearSearch, setClearSearch] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm);
  const debouncedSkillsSearchTerm = useDebounce(skillsSearchTerm);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [selectedEnglishLevel, setSelectedEnglishLevel] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [candidateList, setCandidateList] = useState<Candidate[]>([]);
  const [fromJobSelection, setFromJobSelection] = useState<boolean>(false);

  const title = t('candidates.title');

  const fetchCandidates = useCallback(
    async ({ pageParam = 1 }) => {
      return await getCandidates({
        page: pageParam,
        count: COUNT_ROWS_PER_PAGE,
        fullName: debouncedSearchTerm,
        skills: selectedOptions,
        areaId: selectedArea?.id,
        englishLevel: selectedEnglishLevel || undefined,
        fromJobAssignedDate: startDate || undefined,
        toJobAssignedDate: endDate || undefined,
      });
    },
    [debouncedSearchTerm, selectedOptions, selectedArea?.id, selectedEnglishLevel, startDate, endDate],
  );

  const {
    data: candidatesData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['getCandidates', debouncedSearchTerm, selectedOptions, selectedArea?.id, selectedEnglishLevel, startDate, endDate],
    queryFn: fetchCandidates,
    getNextPageParam: (lastPage) => {
      const nextPage = (lastPage.page || 0) + 1;
      return nextPage <= (lastPage.lastPage || 0) ? nextPage : undefined;
    },
    initialPageParam: 1,
  });

  useEffect(() => {
    setCurrentPage(0);
    setCandidateList([]);
  }, [debouncedSearchTerm, selectedOptions, selectedArea?.id, selectedEnglishLevel, startDate, endDate]);

  useEffect(() => {
    const allCandidates = candidatesData?.pages.flatMap((page) => page.items) || [];

    if (fromJobSelection) {
      setFromJobSelection(false);
    }

    setCandidateList(allCandidates);
  }, [candidatesData]);

  const totalItems = candidatesData?.pages[candidatesData.pages.length - 1]?.totalItems || 0;

  const { data: skills } = useQuery({
    queryKey: ['getSkills', debouncedSkillsSearchTerm],
    queryFn: () =>
      getSkills({
        page: 1,
        count: 10,
        name: debouncedSkillsSearchTerm,
      }),
  });

  const { data: areas = [] } = useQuery<Area[]>({
    queryKey: ['getAreas'],
    queryFn: async () => {
      return await getCandidateAreas();
    },
  });

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSkillsSearchTerm('');
    setSelectedOptions([]);
    setClearSearch(true);
    setSelectedArea(null);
    setSelectedEnglishLevel(null);
    setStartDate(null);
    setEndDate(null);
    setCandidateList([]);
  };

  const invalidateCandidatesQuery = async () => {
    setFromJobSelection(true);
    
    await queryClient.invalidateQueries({
      queryKey: ['getCandidates'],
    });

    await queryClient.refetchQueries({
      queryKey: ['getCandidates'],
      type: 'active',
    });
  };

  return (
    <div>
      <MainLayout
        onCandidatesInvalidateQuery={() =>
          queryClient.invalidateQueries({
            queryKey: [
              'getCandidates',
              debouncedSearchTerm,
              selectedOptions,
              selectedArea?.id,
              selectedEnglishLevel,
              startDate,
              endDate,
            ],
          })
        }
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <h1>{title}</h1>
          <ClearFiltersButton handleClearFilters={handleClearFilters} />
        </Box>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <SearchBar margin="0 0 15px 0" onSearch={handleSearch} clearSearch={clearSearch} />
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'column' }}>
            <AutocompleteSelect
              searchTerm={skillsSearchTerm}
              onSearchTermChange={(newValue: string) => setSkillsSearchTerm(newValue)}
              options={skills?.items.map((skill) => skill.name) ?? []}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
            />
            <Autocomplete
              sx={{ width: '380px' }}
              id="area-autocomplete"
              options={areas.length ? [...areas].sort((a, b) => a.name.localeCompare(b.name)).map((area) => area.name) : []}
              onChange={(_, newValue) => {
                const selected = areas?.find((area) => area.name === newValue);
                setSelectedArea(selected || null);
              }}
              value={selectedArea?.name || ''}
              slotProps={{
                listbox: {
                  style: { maxHeight: 200 },
                },
              }}
              renderInput={(params) => <TextField {...params} placeholder={t('common.area')} />}
            />
            <Autocomplete
              sx={{ width: '280px' }}
              id="english-level-autocomplete"
              options={ENGLISH_LEVELS}
              onChange={(_, newValue) => {
                setSelectedEnglishLevel(newValue);
              }}
              value={selectedEnglishLevel || ''}
              slotProps={{
                listbox: {
                  style: { maxHeight: 200 },
                },
              }}
              renderInput={(params) => <TextField {...params} placeholder={t('common.english_level') || 'English Level'} />}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <DateRangePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </div>

        {candidatesData && (
          <Paper sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CandidateTable
              rows={candidateList}
              loadMore={fetchNextPage}
              loading={isFetchingNextPage}
              hasMore={hasNextPage ?? false}
              totalItems={totalItems}
              onAssignSuccess={invalidateCandidatesQuery}
            />
          </Paper>
        )}
        <Loader isLoading={isLoading} />
      </MainLayout>
    </div>
  );
};
